/**
 * Where a guest was going before we interrupted them.
 *
 * **The problem this solves.** A link to `/schedule/haldi` in the group chat
 * works perfectly for anybody who has used the site before, and lands a
 * first-timer on `/now` having silently discarded the only reason they tapped
 * it. Three redirects sit between the tap and the page — sign-in, the welcome
 * screen, and the name-and-team reveal — and each one used to forget.
 *
 * **Why storage rather than router state.** The obvious fix is to pass the path
 * along in `<Navigate state>`, and it cannot work: `beginSignIn` ends in
 * `window.location.assign` to Cognito's managed login (`tokens.ts`), which is a
 * full page load. Every scrap of in-memory routing state dies there. The
 * `state={{ from }}` already on the signed-out branch of `Guarded` survives
 * exactly as far as the login screen and no further, which is why deep links
 * looked like they half-worked.
 *
 * So this is a breadcrumb in `sessionStorage`, and it is deliberately the same
 * shape as the PKCE verifier sitting beside it: written immediately before a
 * navigation that destroys everything else, scoped to this tab, and consumed
 * once. A guest who opens the link in a second tab gets their own.
 *
 * **Read once, then gone.** `takeIntent` deletes as it reads. A leftover would
 * hijack the *next* welcome — sign out, sign back in, and land on a link
 * somebody sent you yesterday — which is a worse bug than the one this fixes,
 * because it has no obvious cause from the guest's side.
 */
import { resolvePath } from 'react-router-dom';

const KEY = 'mv.intent';

/**
 * The first segment of every route a deep link is allowed to name.
 *
 * **An allowlist because this value is navigated to, and it comes off the URL
 * bar.** Without one, `//example.com` is a path React Router will happily treat
 * as a protocol-relative URL, and `/welcome` would send a guest who has just
 * finished the welcome screen back to the start of it.
 *
 * Segments rather than whole paths, so `/schedule/haldi` and `/gallery/<id>`
 * keep the part that made the link worth sending — naming a specific event is
 * the main thing anybody will do with this.
 *
 * `intent.test.ts` asserts this stays in step with the routes `App.tsx`
 * actually declares, because a list of route names written down twice is a
 * list that will disagree.
 *
 * **`me` carries `/me/guests` with it**, which is the organiser's list. That is
 * deliberate and safe: this list decides what is a *route*, not who may see
 * one, and `AdminGuests` checks `isAdmin` itself. Being sent a link you cannot
 * open is the same non-event here as typing the URL would be.
 */
const ALLOWED = new Set([
  'now', 'schedule', 'map', 'upload', 'gallery', 'scorecard', 'credits', 'me',
  // Deep-linkable all year even though the header only offers it during the
  // week (D62) — sending somebody a single card is most of the point of it.
  'icebreakers',
]);

/**
 * Somewhere we are willing to send somebody, **normalised**, or `null`.
 *
 * **It returns the path rather than a yes, and that is the fix for a real
 * hole.** Checking the string as written and then handing the string as written
 * to the router asks two different questions: React Router resolves dot
 * segments at navigation time, so `/gallery/../welcome` reads as a `gallery`
 * link to a first-segment check and arrives at `/welcome` — the one
 * destination the allowlist names explicitly, because it loops a guest back
 * into the screen they just finished. `/gallery/../../me/guests` reaches the
 * organiser's list by the same trick. Both are verified in the test.
 *
 * So the string is normalised here and the normalised form is what gets stored
 * and handed back. The value that was vetted and the value that is navigated
 * to are then the same string, which closes the shape of the bug rather than
 * the two instances of it.
 *
 * **Normalised by `resolvePath`, which is the router's own function.** The
 * first attempt at this used `new URL` against a placeholder origin, on the
 * assumption that it resolved paths the same way. It does not: `URL` leaves a
 * doubled slash in the middle of a path alone and `resolvePath` collapses it,
 * so `/gallery//me/guests` was stored verbatim and navigated to as
 * `/gallery/me/guests` — two different strings, which is the whole bug again in
 * miniature. Asking the router what it will do beats reimplementing it, and it
 * cannot drift when the router is upgraded.
 */
export function safeIntent(path: string): string | null {
  /**
   * **These three run before `resolvePath` and are load-bearing, not
   * redundant.** `resolvePath` resolves; it does not judge. It happily turns
   * `//anything/../../me/guests` into `/me/guests`, and some browsers read a
   * backslash as a path separator. Dropping any of them because "the allowlist
   * catches it anyway" reopens a route to the organiser's list — each is
   * covered by its own test for that reason.
   */
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return null;

  /**
   * **The catch has no reachable throw path today, and it stays.** It was
   * raised as dead code and deferred to the next change touching this file,
   * which is this one — and the answer on looking properly is that it is
   * insurance rather than decoration. `path` arrives off the URL bar and out of
   * a store the guest can edit, `resolvePath` is third-party code whose
   * behaviour on the next router upgrade is not this file's to promise, and
   * every caller is inside a render. A throw here does not degrade to a lost
   * deep link, which is the failure this module is designed around — it takes
   * out the render that was going to sign somebody in.
   *
   * The cost is three lines that never execute. The cost of being wrong is a
   * white screen at check-in, so this is not a trade worth taking for tidiness.
   */
  let resolved: { pathname: string; search: string; hash: string };
  try {
    resolved = resolvePath(path);
  } catch {
    return null;
  }

  const segment = resolved.pathname.slice(1).split('/')[0];
  if (!ALLOWED.has(segment)) return null;
  // `search` kept — `?at=` pins the clock and is a legitimate thing to send
  // somebody. `hash` too, so the router sees what the sender wrote.
  return resolved.pathname + resolved.search + resolved.hash;
}

/** Remember it, if it is worth remembering. Safe to call repeatedly. */
export function rememberIntent(path: string): void {
  try {
    const safe = safeIntent(path);
    if (safe) sessionStorage.setItem(KEY, safe);
  } catch {
    /* Private mode, or storage full. A deep link is a nicety; losing it must
       never be the thing that stops a guest signing in. */
  }
}

/**
 * Read it and leave it where it is.
 *
 * **For render-time reads, which must not consume.** A component deciding
 * `<Navigate to={…}>` during render is called more than once for the same
 * commit — React does it deliberately in development to surface exactly this
 * kind of impurity — and a consuming read would hand the destination to the
 * first call and `null` to the second, sending the guest to `/now` with the
 * link silently eaten. `takeIntent` belongs in event handlers; this belongs in
 * render.
 *
 * Leaving it behind is safe because the two things that end a deep link's life
 * both still happen: `Welcome` consumes it when a first-timer finishes, and
 * `signOut` clears it. The only way to read a stale one is a second sign-in in
 * the same tab without signing out first, where landing on the same link again
 * is the reasonable answer anyway.
 */
export function peekIntent(): string | null {
  try {
    const stored = sessionStorage.getItem(KEY);
    return stored ? safeIntent(stored) : null;
  } catch {
    return null;
  }
}

/** Read it and forget it. `null` when there is nothing to go back to. */
export function takeIntent(): string | null {
  try {
    const stored = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    // Re-checked on the way out, not only on the way in, and this is the check
    // that has to be right: `rememberIntent` is not the only way a value gets
    // in here. The key is a string in a store the guest can edit, and the
    // allowlist can shrink between a write and a read — a released build
    // removing a route meets the previous build's breadcrumb in a tab nobody
    // reloaded.
    return stored ? safeIntent(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Where a returning guest goes once they are signed in.
 *
 * **One helper for both screens, because two of them disagreed.** `Login` and
 * `AuthCallback` each decided this inline and had put the two carriers in
 * opposite orders, under comments claiming they matched. They only differ when
 * there is a stale breadcrumb *and* a fresh `from`, which is a rare enough case
 * to get wrong twice and never notice.
 *
 * Router state wins where it exists, because it can only have come from a
 * `Guarded` bounce in this tab moments ago, and the breadcrumb may be older
 * than that. After managed login there is no router state at all — that is the
 * whole reason the breadcrumb exists — so `AuthCallback` reaches the second
 * branch essentially always.
 *
 * **`from` is vetted too.** It is a second string feeding the same `<Navigate>`
 * and arrives from the same place, so it has no more claim to be trusted than
 * the stored one. It was going through unchecked, which was not reachable today
 * and is not an invariant worth resting on.
 *
 * `peek`, not `take`: both call sites decide this during render.
 */
export function onwardTo(from?: string): string {
  return (from ? safeIntent(from) : null) ?? peekIntent() ?? '/now';
}

/** Drop it unread. Sign-out does this: the next person at this phone is not
 *  who the link was sent to. */
export function clearIntent(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* See `rememberIntent`. */
  }
}
